import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  type DoubleBookingConflict,
  asDoubleBookingConflict,
} from '../create-appointment/double-booking-conflict';
import type {
  CreateAppointmentVisitInput,
  CreatedAppointmentVisit,
} from '../types';

interface UseCreateAppointmentVisitOptions {
  onSuccess?: (visit: CreatedAppointmentVisit) => void;
  onError?: (error: Error) => void;
  /**
   * A clash on any service in the visit. Same handshake as a single booking:
   * ask, then re-send with `allowDoubleBooking: true` (ENG-792).
   */
  onDoubleBookingConflict?: (conflict: DoubleBookingConflict) => void;
}

/**
 * `POST /appointments/visit` — several services back to back for one client,
 * each with its own team member. Invalidates what a single booking does, since
 * a visit is several of them.
 */
export const useCreateAppointmentVisit = (
  options?: UseCreateAppointmentVisitOptions
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (input: CreateAppointmentVisitInput) =>
      apiClient.post<CreatedAppointmentVisit>('appointments/visit', input),
    onSuccess: (visit, input) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.all(),
      });
      if (input.clientPackageItemId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.clientPackages.all(),
        });
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.resources.allAllocations(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.resources.allUtilisation(),
      });
      toast.success(
        `Appointment created with ${visit.appointments.length} services`
      );
      options?.onSuccess?.(visit);
    },
    onError: (error: Error) => {
      const conflict = options?.onDoubleBookingConflict
        ? asDoubleBookingConflict(error)
        : null;
      if (conflict) {
        options?.onDoubleBookingConflict?.(conflict);
        return;
      }
      toast.error(error.message || 'Failed to create appointment');
      options?.onError?.(error);
    },
  });

  return {
    createAppointmentVisit: mutation.mutate,
    isCreatingVisit: mutation.isPending,
  };
};
